import Application from '../Application';
import BaseView, { DomainModel3D } from './BaseView';
import HandleView from './HandleView';
import logging from '../logging';
import pc from '../ParametricCache';
import { BufferGeometry, Group, Mesh, Object3D, Material } from 'three';
import { Drawer } from '../domain';

const log = logging('view');

type XYZ = {x: number, y: number, z: number};
const set = (vec: XYZ, x: number, y: number, z: number) => {
    vec.x = x;
    vec.y = y;
    vec.z = z;
}

export default class DrawerView extends BaseView {

    declare model: Drawer;

    front: Group;
    panel: Mesh;
    frame?: Mesh;
    box: Mesh;
    slide: Group;

    handles: Array<HandleView>;

    constructor(model: DomainModel3D, app: Application) {
        super(model, app);
        this.init();
    }

    make() {
        this.handles = [];
        const drawer = this.model;

        this.front = new Group();
        this.panel = new Mesh(new BufferGeometry(), new Material());
        this.panel.castShadow = this.panel.receiveShadow = true;

        this.panel.name = 'Drawer Panel';
        this.panel.userData.isSelectable = true;
        this.panel.userData.isMovable = true;
        this.front.add(this.panel);
        this.highlights.add(this.panel);

        if(drawer.frame) {
            this.frame = new Mesh(new BufferGeometry(), new Material());
	    this.frame.castShadow = this.frame.receiveShadow = true;

            this.frame.name = 'Drawer Frame';
            this.frame.userData.isSelectable = true;
            this.frame.userData.isMovable = true;
            this.front.add(this.frame);
            this.highlights.add(this.frame);
        }

        this.box = new Mesh(new BufferGeometry(), new Material());
        this.box.name = 'DrawerBox';

        this.name = 'Drawer';

        this.slide = new Group()
        this.slide.name = 'Slide';

        this.add(this.slide);
        this.slide.add(this.box);
        this.slide.add(this.front);

        for(let handle of drawer.handles) {
            const view = new HandleView(handle, this.app);
            this.handles.push(view);
            this.slide.add(view);
        }

        if(this.isBoxOnly()) {
            for(let child of this.slide.children) {
                child.position.z = -0.75;
            }
        }

        this.userData.isParametric = true;
    }

    bind() {

        if(this.isBoxOnly()) {
            this.bindMaterial(this.panel, this.model.box.material);
        } else {
            this.bindMaterial(this.panel, this.model.panel.material);
        }
        if(this.frame) {
            this.bindMaterial(this.frame, this.model.frame.material);
        }
        this.bindMaterial(this.box, this.model.box.material);

        const changeSize = this.changeSize.bind(this);
        this.model.bind('width', changeSize);
        this.model.on('height', changeSize);
        this.model.on('depth', changeSize);

        this.model.bind('open', () => {
            this.slide.position.z = this.model.open * this.model.depth;
        });

        this.model.bind('opacity', () => {
            const castShadow = this.model.opacity == 1;
            if(this.frame) this.frame.castShadow = castShadow;
            this.panel.castShadow = castShadow;
            this.box.castShadow = castShadow;
            for(const handle of this.model.handles) {
                handle.opacity = this.model.opacity;
            }
        });
    }

    private async changeSize() {
        const drawer = this.model;
        this.model.box.width = this.model.width - 1;
        this.model.box.height = this.model.height - 1;
        this.model.box.depth = this.model.depth;
        for(let model of this.model.handles) {
            // full width handles have width and height, other types ignore it
            model.width = this.model.width;
            model.height = this.model.height;
        }

        this.updates.queue(
            async () => { 
                let front: BufferGeometry;
                if(drawer.scoop) {
                    front = await pc.make(
                        'ScoopFront', drawer.width - 1, drawer.height - 1);
                } else if(drawer.panel == null || drawer.panel.style == null) {
                    front = await pc.make(
                        'FlatFront', drawer.width - 1, drawer.height - 1);
                } else {
                    front = await pc.make('FrameAndPanel',
                        (drawer.frame && drawer.frame.style) ?
                            drawer.frame.style : null,
                        drawer.panel.style,
                        drawer.width, drawer.height,
                        drawer.panel.grain);

                }
                const box = await pc.make('DrawerBox',
                    drawer.box.width,
                    drawer.box.height,
                    (this.isBoxOnly())? drawer.box.depth - 0.75 : drawer.box.depth,
                    drawer.box.front);
                return [front, box];
            },
            ([frameAndPanel, boxGeo]) => {
                const [frameGeo, panelGeo] =
                    frameAndPanel as any as BufferGeometry[];
                if(this.frame && frameGeo) {
                    this.frame.geometry = frameGeo;
                }
                this.panel.geometry = panelGeo;

                this.box.geometry = boxGeo;

                const box = this.box;
                box.position.x = (drawer.width - drawer.box.width) / 2;
                box.position.y = (drawer.height - drawer.box.height) / 2;
                // push it forward just a bit so it intersects the panel
                box.position.z = 0.0625 - drawer.box.depth;

                if(this.isBoxOnly()) {
                    this.panel.position.x = box.position.x;
                    this.panel.position.y = box.position.y;
                }

                for(let handle of this.model.handles) {
                    if(handle.isExtrusionPull
                       || handle.style?.includes('pull-elite'))
                    {
                        let side = handle.side;
                        if(side == null) side = 'top';
                        switch(side) {
                            case "left":
                                set(handle.position, 0, handle.height, 0);
                                set(handle.scale, handle.height, 1, 1);
                                set(handle.rotation, 0, 0, 1.5*Math.PI);
                                break;
                            case "right":
                                set(handle.position, handle.width, 0, 0);
                                set(handle.scale, handle.height, 1, 1);
                                set(handle.rotation, 0, 0, 0.5*Math.PI);
                                break;
                            case "top":
                                set(handle.position, handle.width, handle.height, 0);
                                set(handle.scale, handle.width, 1, 1);
                                set(handle.rotation, 0, 0, Math.PI);
                                break;
                            case "bottom":
                                set(handle.position, 0, 0, 0);
                                set(handle.scale, handle.width, 1, 1);
                                set(handle.rotation, 0, 0, 0);
                                break;
                            default:
                                log.error(`Unsupported full-width obj side: ${handle.side}`);
                        }
                    } else {
                        handle.position.x = this.model.width / 2;
                        handle.position.y = this.model.height / 2 - 0.25;
                        if(handle.assetId) {
                            handle.position.z = 0.75;
                        }
                    }
                }

                this.pipelineUpdate();
            }
        );
    }

    isBoxOnly() {
        return this.model.scoop || this.model.panel == null || this.model.panel.style == null;
    }

}
